import React, { useMemo, useRef, useState, useCallback } from 'react'
import { Arrow } from '@app/icons'
import { useToggle, useClickOutside } from '@/shared'
import { Options } from './options'
import styles from './SelectTree.module.scss'
import { InputWithIcon } from '@/ui'
import { getCurrentLanguage } from '@/i18next'
import { useTranslation } from 'react-i18next'
import { Close8 } from '@app/images'

export const SelectTree = ({
	options = [],
	value,
	placeholder,
	showItems = 5,
	onChange,
	onBlur,
	required = false,
	className = '',
	inputClasses = '',
	iconClasses = '',
	optionsStyles,
	...props
}) => {
	const { t } = useTranslation()
	const [isOpenedOptions, toggleOpenedOptions] = useToggle(false)
	const [searchQuery, setSearchQuery] = useState('')
	const currentLanguage = getCurrentLanguage()
	const selectRef = useRef(null)

	// Определяем родителей без потомков с использованием Set для оптимизации
	const parentsWithoutChildren = useMemo(() => {
		// Принудительно считаем "глобальные" корни бездетными
		const forcedParents = new Set(
			options.filter((option) => option.parentId === null && option.forceNoChildren).map((option) => option.id)
		)

		// Все корни
		const parentIds = options.filter((option) => option.parentId === null).map((option) => option.id)

		// Родители, у которых есть дети по факту в списке
		const parentsWithChildren = new Set()
		options.forEach((option) => {
			if (option.parentId !== null) {
				parentsWithChildren.add(option.parentId)
			}
		})

		// Родители без детей + принудительно помеченные как бездетные
		return parentIds.filter((id) => forcedParents.has(id) || !parentsWithChildren.has(id))
	}, [options])

	// Текущий выбранный элемент
	const activeItem = useMemo(() => {
		if (!value) return null
		const val = options.find(({ code }) => code === value)
		if (val) {
			const lang = currentLanguage === 'rus' ? 'nameRu' : 'nameKz'
			return val[currentLanguage] || val[lang] || null
		}
		return null
	}, [value, options, currentLanguage])

	// Вспомогательная функция для проверки совпадения текста в опции
	const optionHasMatch = useCallback(
		(option, searchText) => {
			const name = option[currentLanguage] || option[currentLanguage === 'rus' ? 'nameRu' : 'nameKz'] || ''
			return name.toLowerCase().includes(searchText.toLowerCase())
		},
		[currentLanguage]
	)

	const filteredOptions = useMemo(() => {
		if (!searchQuery.trim()) {
			return options
		}

		const searchLower = searchQuery.toLowerCase()
		const matchedOptions = new Set()

		// Функция для добавления всех дочерних элементов родителя
		const addAllChildren = (parentId) => {
			const children = options.filter((o) => o.parentId === parentId)
			children.forEach((child) => {
				matchedOptions.add(child)
				// Рекурсивно добавляем всех потомков
				addAllChildren(child.id)
			})
		}

		// Функция для обработки иерархии опций - добавляет элементы в результаты
		// и возвращает true, если элемент или его потомки соответствуют поиску
		const processOptionHierarchy = (option, isDirectMatch) => {
			// Добавляем элемент, если он напрямую соответствует запросу
			if (isDirectMatch) {
				matchedOptions.add(option)
			}

			// Если это родительский элемент
			if (option.parentId === null) {
				// Если родитель соответствует поиску, добавляем всех его потомков
				if (isDirectMatch) {
					addAllChildren(option.id)
					return true
				}

				// Проверяем его дочерние элементы
				const children = options.filter((o) => o.parentId === option.id)
				let hasMatchingChild = false

				children.forEach((child) => {
					// Проверяем совпадение в дочернем элементе
					const childMatches = optionHasMatch(child, searchLower)
					// Рекурсивно проверяем его потомков
					const childHasMatchingDescendants = processOptionHierarchy(child, childMatches)

					if (childMatches || childHasMatchingDescendants) {
						hasMatchingChild = true
					}
				})

				// Если дочерние элементы соответствуют поиску, добавляем родителя
				if (hasMatchingChild) {
					matchedOptions.add(option)
				}

				return isDirectMatch || hasMatchingChild
			}
			// Для дочерних элементов с совпадением, добавляем родителей
			else if (isDirectMatch) {
				// Итерируем вверх по иерархии, добавляя всех родителей
				let currentOptionId = option.parentId
				while (currentOptionId !== null) {
					const parent = options.find((o) => o.id === currentOptionId)
					if (parent) {
						matchedOptions.add(parent)
						currentOptionId = parent.parentId
					} else {
						break
					}
				}
				return true
			}

			return false
		}

		// Запускаем обработку с корневых элементов
		options
			.filter((option) => option.parentId === null)
			.forEach((parent) => {
				const parentMatches = optionHasMatch(parent, searchLower)
				processOptionHierarchy(parent, parentMatches)
			})

		return Array.from(matchedOptions)
	}, [options, searchQuery, optionHasMatch])

	// Определяем родителей, которые должны быть раскрыты при поиске

	// Определяем родителей, которые должны быть раскрыты при поиске
	const parentsToExpand = useMemo(() => {
		if (!searchQuery.trim()) return []

		const matchingParents = new Set()
		const searchLower = searchQuery.toLowerCase()

		// Создаем Map для быстрого поиска элементов по ID (переиспользуем логику)
		const optionsMap = new Map(options.map((option) => [option.id, option]))

		// Функция для проверки совпадений и добавления в список раскрытия
		const processParentForExpansion = (parentId) => {
			const parent = optionsMap.get(parentId)
			if (!parent) return false

			// Если родитель соответствует поиску, раскрываем его
			if (optionHasMatch(parent, searchLower)) {
				matchingParents.add(parentId)
				return true
			}

			// Проверяем дочерние элементы
			const children = options.filter((o) => o.parentId === parentId)
			let hasMatchingChild = false

			children.forEach((child) => {
				// Если дочерний элемент соответствует поиску
				if (optionHasMatch(child, searchLower)) {
					hasMatchingChild = true
				} else {
					// Рекурсивно проверяем потомков дочернего элемента
					if (processParentForExpansion(child.id)) {
						hasMatchingChild = true
					}
				}
			})

			// Если найдено совпадение в потомках, добавляем родителя в список для раскрытия
			if (hasMatchingChild) {
				matchingParents.add(parentId)
			}

			return hasMatchingChild
		}

		// Обрабатываем все корневые элементы
		options
			.filter((o) => o.parentId === null)
			.forEach((parent) => {
				processParentForExpansion(parent.id)
			})

		return Array.from(matchingParents)
	}, [options, searchQuery, optionHasMatch])

	// Отображаемое значение в поле ввода
	const displayValue = useMemo(() => {
		return searchQuery || activeItem || ''
	}, [searchQuery, activeItem])

	// Мемоизируем обработчики для предотвращения ненужных рендеров
	const onSelect = useCallback(
		(code) => {
			onChange(code)
			setSearchQuery('')
			toggleOpenedOptions(false)
		},
		[onChange, toggleOpenedOptions]
	)

	const handleInputChange = useCallback(
		(inputValue) => {
			setSearchQuery(inputValue)
			if (!isOpenedOptions) {
				toggleOpenedOptions(true)
			}
		},
		[isOpenedOptions, toggleOpenedOptions]
	)

	const onClear = useCallback(() => {
		onChange('')
		setSearchQuery('')
	}, [onChange])

	const onClickOutside = useCallback(() => {
		toggleOpenedOptions(false)
		setSearchQuery('')
	}, [toggleOpenedOptions])

	useClickOutside(selectRef, isOpenedOptions, onClickOutside)

	const hasValue = value || searchQuery

	// Находим текущий выбранный элемент для отображения иконки
	const currentSelectedElement = useMemo(() => options.find((item) => item.code === value), [options, value])

	return (
		<div className={`${styles.select} ${className}`} ref={selectRef}>
			<InputWithIcon
				value={displayValue}
				onChange={handleInputChange}
				placeholder={placeholder || t('select_from_list')}
				onClick={() => toggleOpenedOptions()}
				iconSide="right"
				required={required}
				inputClasses={inputClasses}
				iconClasses={iconClasses}
				icon={
					hasValue ? (
						<img
							onClick={(e) => {
								e.stopPropagation()
								onClear()
							}}
							src={Close8}
							className={styles.closeIcon}
							alt="clear"
						/>
					) : (
						<Arrow
							styles={{ fill: '#AAB8D1', transform: `rotate(${isOpenedOptions ? '90deg' : '-90deg'})`, width: '10px' }}
						/>
					)
				}
				valueIcon={currentSelectedElement?.icon}
				autoComplete="off"
				{...props}
			/>
			{isOpenedOptions && (
				<Options
					onSelect={onSelect}
					options={filteredOptions}
					showItems={showItems}
					onBlur={onBlur}
					optionsStyles={optionsStyles}
					searchQuery={searchQuery}
					expandedParents={parentsToExpand}
					parentsWithoutChildren={parentsWithoutChildren}
				/>
			)}
		</div>
	)
}
